import React, { useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { Trans } from "react-i18next";

import { useTranslation } from "@refinedev/core";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import { FormField, MultiAutocompleteInput } from "@/components/forms";
import { useOrganizationPractitionersContext } from "@/contexts";
import { useAllPractitionerRoles } from "@/hooks/useAllPractitionerRoles";
import { useClinics } from "@/hooks/useClinics";
import { formatHumanName, formatters } from "@/utils";
import { FhirResourceType, extractFhirResourceId } from "@/utils";

import { fhirR4 } from "@smile-cdr/fhirts";

import { BroadcastMessageFilterEnum, BroadcastMessageFormValues } from "./MessageBroadcastModal";

const SENDER_NAME_MAX_LENGTH = 50;
const MESSAGE_MAX_LENGTH = 4_000;

const HUMAN_NAME_PARTS: ("given" | "family")[] = ["given", "family"];

interface MessageBroadcastModalFieldsProps {
  /** Is the query being fetched? */
  chatRoomLoading: boolean;
  /** Callback function to execute chat room query */
  onQueryForChatRooms: () => void;
}

export const MessageBroadcastModalFields: React.FC<MessageBroadcastModalFieldsProps> = ({
  chatRoomLoading,
  onQueryForChatRooms: handleChatRoomQuery,
}) => {
  const { translate } = useTranslation();
  const { control, watch } = useFormContext<BroadcastMessageFormValues>();
  const [filterValue, clinicIdValues = [], practitionerIdValues = [], numOfChatRooms] = watch([
    "filter",
    "clinic_ids",
    "practitioner_ids",
    "chat_room_count",
  ]);

  const {
    clinics: orgClinics,
    organizationReference,
    practitionerResources,
    isLoading: isOrgLoading,
  } = useOrganizationPractitionersContext();
  const { allPractitionerRoles, isLoading: isPractitionerRolesLoading } =
    useAllPractitionerRoles(practitionerResources);
  const parentOrgId = extractFhirResourceId(organizationReference, FhirResourceType.Organization);

  const { clinics, isLoading: isClinicsLoading } = useClinics(parentOrgId, { fetchAllPages: true });
  const loading = isOrgLoading || isClinicsLoading || isPractitionerRolesLoading;

  // ============================================================================================================
  // Filter, Clinic, and Practitioner Options
  // ============================================================================================================

  const filterOptions = Object.entries(BroadcastMessageFilterEnum).map(([, v]) => v);

  const clinicOptions = useMemo(
    () => clinics.map(({ id, label, value }) => ({ id, value, label })).sort((a, b) => a.label.localeCompare(b.label)),
    [clinics]
  );

  const practitionerOptions = useMemo(() => {
    const parentOrgId = extractFhirResourceId(organizationReference, FhirResourceType.Organization);
    const clinicIdToClinicNameMap = orgClinics.reduce<Record<string, string>>((acc, curr) => {
      if (curr.id && curr.active && curr.id !== parentOrgId) {
        acc[curr.id] = curr.label;
      }
      return acc;
    }, {});
    const activePractitionerRolesFromClinics = allPractitionerRoles.filter(
      ({ active, organization }) => active && organization?.reference !== parentOrgId
    );
    const { pracRoleIdToOrgIdsMap, pracRoleIdToOrgNamesMap, pracIdToPracRoleIdsMap } =
      activePractitionerRolesFromClinics.reduce<{
        pracRoleIdToOrgNamesMap: Record<string, string[]>; // {'prac-role-id-2': ['Org 4 Name', 'Org 5 Name']}
        pracRoleIdToOrgIdsMap: Record<string, string[]>; // {'prac-role-id-2': ['org-id-4', 'org-id-5']}
        pracIdToPracRoleIdsMap: Record<string, string[]>; // {'prac-id-1': ['prac-role-id-2', 'prac-role-id-3']}
      }>(
        (acc, curr) => {
          const clinicId = extractFhirResourceId(curr.organization?.reference, FhirResourceType.Organization);
          if (clinicId === parentOrgId || !curr.id) {
            return acc;
          }

          const { pracRoleIdToOrgIdsMap, pracRoleIdToOrgNamesMap, pracIdToPracRoleIdsMap } = acc;
          const fhir_practitioner_id = extractFhirResourceId(
            curr.practitioner?.reference,
            FhirResourceType.Practitioner
          );
          if (fhir_practitioner_id) {
            pracIdToPracRoleIdsMap[fhir_practitioner_id] = [
              ...(pracIdToPracRoleIdsMap[fhir_practitioner_id] ?? []),
              curr.id,
            ];
          }

          if (clinicId) {
            pracRoleIdToOrgIdsMap[curr.id] = [...(pracRoleIdToOrgIdsMap[curr.id] ?? []), clinicId];
            const clinicName = clinicId in clinicIdToClinicNameMap ? clinicIdToClinicNameMap[clinicId] : undefined;
            if (clinicName) {
              pracRoleIdToOrgNamesMap[curr.id] = [...(pracRoleIdToOrgNamesMap[curr.id] ?? []), clinicName];
            }
          }
          return acc;
        },
        {
          pracRoleIdToOrgNamesMap: {},
          pracRoleIdToOrgIdsMap: {},
          pracIdToPracRoleIdsMap: {},
        }
      );

    const practitionerOptions = practitionerResources
      .map(({ id, name }) => {
        const fhirPractitionerId = id ?? "";
        const practitionerRoleIds = pracIdToPracRoleIdsMap[fhirPractitionerId] ?? [];
        const { clinicIds, clinicNames } = practitionerRoleIds.reduce<{ clinicIds: string[]; clinicNames: string[] }>(
          (acc, pracRoleId) => {
            const { clinicIds, clinicNames } = acc;
            const id = pracRoleIdToOrgIdsMap[pracRoleId];
            const name = pracRoleIdToOrgNamesMap[pracRoleId];
            if (id) {
              clinicIds.push(...id);
            }
            if (name) {
              clinicNames.push(...name);
            }
            return acc;
          },
          {
            clinicNames: [],
            clinicIds: [],
          }
        );

        return {
          id: fhirPractitionerId,
          label: formatHumanName(name?.[0] as fhirR4.HumanName, {
            include: HUMAN_NAME_PARTS,
            lineSeparator: " ",
          }),
          value: formatters.formatPractitioner(id ?? ""),
          clinicNames,
          clinicIds,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
    return practitionerOptions;
  }, [allPractitionerRoles, orgClinics, organizationReference, practitionerResources]);

  // ============================================================================================================
  // Filtered options for practitioner and clinic (ex. select clinic first, a subset of practitioner options are shown)
  // ============================================================================================================

  // Filter by clinic: only show practitioners belonging to selected clinics
  const filteredPractitionerOptions =
    filterValue === BroadcastMessageFilterEnum.CLINIC && clinicIdValues.length > 0
      ? practitionerOptions.filter(prac => {
          const selectedClinicIds = new Set(clinicIdValues.map(c => c.id));
          return prac.clinicIds?.some(id => selectedClinicIds.has(id));
        })
      : practitionerOptions;

  // Filter by practitioner: only show clinics that contain selected practitioners
  const filteredClinicOptions =
    filterValue === BroadcastMessageFilterEnum.PRACTITIONER && practitionerIdValues.length > 0
      ? (() => {
          const selectedPracIds = new Set(practitionerIdValues.map(p => p.id));
          const clinicIdsForSelectedPracs = new Set(
            practitionerOptions.filter(prac => selectedPracIds.has(prac.id)).flatMap(prac => prac.clinicIds)
          );
          return clinicOptions.filter(clinic => clinicIdsForSelectedPracs.has(clinic.id));
        })()
      : clinicOptions;

  return (
    <>
      {/* Step 1: Select which chat rooms to send the public announcement to */}
      <Box sx={{ mb: 2 }}>
        <FormField label={translate("messenger.broadcastMessage.filterInput.title")}>
          <Controller
            name="filter"
            control={control}
            rules={{ required: translate("messenger.broadcastMessage.required.filter") }}
            render={({ field }) => (
              <Autocomplete
                data-testid="message-broadcast-filter-select"
                disabled={chatRoomLoading && filterValue === BroadcastMessageFilterEnum.ALL}
                disableClearable={true}
                getOptionLabel={option => translate(`messenger.broadcastMessage.filterInput.optionTitle.${option}`)}
                multiple={false}
                onChange={(_event, newValue) => field.onChange(newValue)}
                options={filterOptions}
                popupIcon={<ExpandMoreIcon sx={({ palette }) => ({ color: palette.grey[500] })} />}
                renderInput={params => (
                  <TextField
                    {...params}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <React.Fragment>
                          {chatRoomLoading && filterValue === BroadcastMessageFilterEnum.ALL ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                        </React.Fragment>
                      ),
                    }}
                    data-testid="message-broadcast-filter-select"
                    placeholder={translate("messenger.broadcastMessage.filterInput.placeholder")}
                  />
                )}
                renderOption={(props, option /*, { selected }*/) => {
                  return (
                    <li {...props} key={`send-announcement-to-${option}`}>
                      <Stack spacing={0}>
                        <Typography>
                          {translate(`messenger.broadcastMessage.filterInput.optionTitle.${option}`)}
                        </Typography>
                        <Typography variant="caption">
                          {translate(`messenger.broadcastMessage.filterInput.optionSubtitle.${option}`)}
                        </Typography>
                      </Stack>
                    </li>
                  );
                }}
                size="small"
                value={field.value ?? undefined}
              />
            )}
          />
        </FormField>
      </Box>

      {(filterValue === BroadcastMessageFilterEnum.CLINIC ||
        filterValue === BroadcastMessageFilterEnum.PRACTITIONER) && (
        <Stack spacing={2}>
          <Stack
            direction={filterValue === BroadcastMessageFilterEnum.CLINIC ? "column" : "column-reverse"}
            spacing={2}
          >
            <FormField
              label={translate("messenger.broadcastMessage.clinicInput.title")}
              required={filterValue === BroadcastMessageFilterEnum.CLINIC}
              showOptionalTag
            >
              <Controller
                name="clinic_ids"
                control={control}
                render={({ field, fieldState }) => {
                  const showError = !!fieldState.error && (fieldState.isTouched || fieldState.isDirty);

                  return (
                    <MultiAutocompleteInput
                      async={{ loading }}
                      data-testid="broadcast-message-clinic-ids-autocomplete"
                      disabled={
                        filterValue === BroadcastMessageFilterEnum.PRACTITIONER && practitionerIdValues.length === 0
                      }
                      disableCloseOnSelect
                      error={showError}
                      getOptionLabel={option => option.label}
                      groupBy={option => option.label.at(0)?.toUpperCase() ?? ""}
                      helperText={showError ? fieldState.error?.message : undefined}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      options={filteredClinicOptions}
                      placeholder={
                        filterValue === BroadcastMessageFilterEnum.PRACTITIONER
                          ? practitionerIdValues.length > 0
                            ? translate("messenger.broadcastMessage.clinicInput.practitionerClinicsPlaceholder", {
                                count: filteredClinicOptions.length,
                              })
                            : translate("messenger.broadcastMessage.clinicInput.noPractitionersPlaceholder")
                          : translate("messenger.broadcastMessage.clinicInput.defaultPlaceholder")
                      }
                      onBlur={field.onBlur}
                      onChange={newValue => field.onChange(newValue)}
                      renderTags={(value, getTagProps) => (
                        <>
                          {value.map((option, index) => {
                            const { id, label } = option;
                            return (
                              <Chip
                                {...getTagProps({ index })}
                                key={`selected-chat-announcement-clinic-${id}`}
                                label={<Typography variant="caption">{label}</Typography>}
                              />
                            );
                          })}
                        </>
                      )}
                      value={field.value ?? []}
                    />
                  );
                }}
              />
            </FormField>

            <FormField
              label={translate("messenger.broadcastMessage.practitionerInput.title")}
              required={filterValue === BroadcastMessageFilterEnum.PRACTITIONER}
              showOptionalTag
            >
              <Controller
                name="practitioner_ids"
                control={control}
                render={({ field, fieldState }) => {
                  const showError = !!fieldState.error && (fieldState.isTouched || fieldState.isDirty);

                  return (
                    <MultiAutocompleteInput
                      async={{ loading }}
                      data-testid="broadcast-message-practitioner-ids-autocomplete"
                      disabled={filterValue === BroadcastMessageFilterEnum.CLINIC && clinicIdValues.length === 0}
                      disableCloseOnSelect
                      error={showError}
                      getOptionLabel={option => option.label}
                      groupBy={option => option.label.at(0)?.toUpperCase() ?? ""}
                      helperText={showError ? fieldState.error?.message : undefined}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      options={filteredPractitionerOptions}
                      placeholder={
                        filterValue === BroadcastMessageFilterEnum.CLINIC
                          ? clinicIdValues.length > 0
                            ? translate("messenger.broadcastMessage.practitionerInput.clinicPractitionersPlaceholder", {
                                count: filteredPractitionerOptions.length,
                              })
                            : translate("messenger.broadcastMessage.practitionerInput.noClinicsPlaceholder")
                          : translate("messenger.broadcastMessage.practitionerInput.defaultPlaceholder")
                      }
                      onBlur={field.onBlur}
                      onChange={newValue => field.onChange(newValue)}
                      renderOption={(props, option /*, { selected } */) => {
                        const { id, label, clinicNames } = option;
                        return (
                          <li {...props} key={`fhir-practitioner-${id}`}>
                            <Stack spacing={0}>
                              <Typography>{label}</Typography>
                              {clinicNames && <Typography variant="caption">{clinicNames.join(", ")}</Typography>}
                            </Stack>
                          </li>
                        );
                      }}
                      renderTags={(value, getTagProps) => (
                        <>
                          {value.map((option, index) => {
                            const { id, label } = option;
                            return (
                              <Chip
                                {...getTagProps({ index })}
                                key={`selected-chat-announcement-practitioner-${id}`}
                                label={<Typography variant="caption">{label}</Typography>}
                              />
                            );
                          })}
                        </>
                      )}
                      value={field.value ?? []}
                    />
                  );
                }}
              />
            </FormField>
          </Stack>

          <Button
            disabled={loading || !(clinicIdValues.length > 0 || practitionerIdValues.length > 0)}
            loading={chatRoomLoading}
            loadingPosition="end"
            onClick={handleChatRoomQuery}
            size="small"
            sx={{ whiteSpace: "normal", width: "fit-content", height: "fit-content" }}
            variant="outlinedRect"
          >
            {translate("messenger.broadcastMessage.filterInput.filter")}
          </Button>
        </Stack>
      )}

      {/* Display the number of chat rooms this message will be sent to */}
      {typeof numOfChatRooms === "number" && !loading && !chatRoomLoading && (
        <Typography>
          {filterValue === BroadcastMessageFilterEnum.ALL ? (
            translate("messenger.broadcastMessage.sentTo.broadcastToAll", {
              count: numOfChatRooms,
            })
          ) : numOfChatRooms === 0 ? (
            translate("messenger.broadcastMessage.sentTo.broadcast", { count: 0 })
          ) : (
            <Trans
              i18nKey={`messenger.broadcastMessage.sentTo.${
                practitionerIdValues.length > 0 && clinicIdValues.length > 0
                  ? "broadcastBoth"
                  : practitionerIdValues.length > 0
                    ? "broadcastPractitioner"
                    : "broadcastClinic"
              }`}
              count={numOfChatRooms}
              values={{
                practitionerFilter: translate("messenger.broadcastMessage.sentTo.practitioner", {
                  count: practitionerIdValues.length,
                }),
                clinicFilter: translate("messenger.broadcastMessage.sentTo.clinic", {
                  count: clinicIdValues.length,
                }),
              }}
              components={{ bold: <strong /> }}
            />
          )}
        </Typography>
      )}

      {/* Step 2: Write the message */}
      {(numOfChatRooms ?? 0) > 0 && (
        <>
          <Divider sx={{ mx: 2, mt: 1, mb: 1 }} />

          <Box flex="1">
            <FormField label={translate("messenger.broadcastMessage.senderInput.title")} required>
              <Controller
                name="sender"
                control={control}
                rules={{
                  required: translate("messenger.broadcastMessage.required.sender"),
                  maxLength: {
                    value: SENDER_NAME_MAX_LENGTH,
                    message: translate("messenger.broadcastMessage.valid.sender.maxLength", {
                      count: SENDER_NAME_MAX_LENGTH,
                    }),
                  },
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    variant="outlined"
                    size="small"
                    fullWidth
                    data-testid="messenger-sender-name-input"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message || " "}
                    placeholder={translate("messenger.broadcastMessage.senderInput.placeholder", {
                      // TODO: Use account's name
                      name: translate("messenger.broadcastMessage.senderInput.defaultName"),
                    })}
                  />
                )}
              />
            </FormField>
          </Box>

          <Box flex="1">
            <FormField label={translate("messenger.broadcastMessage.messageInput.title")} required>
              <Controller
                name="message"
                control={control}
                rules={{
                  required: translate("messenger.broadcastMessage.required.message"),
                  validate: {
                    maxLength: (value: string) =>
                      value.trim().length <= MESSAGE_MAX_LENGTH ||
                      translate("messenger.broadcastMessage.valid.message.maxLength", {
                        count: MESSAGE_MAX_LENGTH,
                      }),
                  },
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    multiline
                    minRows={1}
                    maxRows={10}
                    placeholder={translate("messenger.broadcastMessage.messageInput.placeholder")}
                    variant="outlined"
                    size="small"
                    fullWidth
                    data-testid="messenger-system-message-input"
                    error={!!fieldState.error}
                    helperText={
                      <Stack component="span">
                        <Typography component="span" textAlign="right" variant="caption">
                          {translate("messenger.broadcastMessage.messageInput.limit", {
                            count: field.value.trim().length,
                            limit: new Intl.NumberFormat().format(MESSAGE_MAX_LENGTH),
                          })}
                        </Typography>

                        {fieldState.error?.message || " "}
                      </Stack>
                    }
                    sx={{
                      "& .MuiInputBase-root": { height: "auto", alignItems: "flex-start" },
                      "& .MuiInputBase-inputMultiline": { height: "auto" },
                    }}
                  />
                )}
              />
            </FormField>
          </Box>
        </>
      )}
    </>
  );
};
